import React, { useState, useEffect, useCallback } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import PropTypes from "prop-types";
import { projectsData } from "./projectsData";
import Contacts from "./Contacts";

export const ProjectCarousel = ({ title, images, autoplayDelay = 3000 }) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  const hasMultipleImages = images && images.length > 1;

  const goToNext = useCallback(() => {
    if (!hasMultipleImages) return;
    const isLastSlide = currentIndex === images.length - 1;
    const newIndex = isLastSlide ? 0 : currentIndex + 1;
    setCurrentIndex(newIndex);
  }, [currentIndex, images.length, hasMultipleImages]);

  const goToSlide = (slideIndex) => {
    setCurrentIndex(slideIndex);
  };

  useEffect(() => {
    if (!hasMultipleImages) return;

    const intervalId = setInterval(() => {
      goToNext();
    }, autoplayDelay);

    return () => clearInterval(intervalId);
  }, [goToNext, autoplayDelay, hasMultipleImages]);

  if (!images || images.length === 0) {
    return null;
  }

  return (
    <div className="mt-8">
      {title && (
        <h3 className="text-xl font-semibold text-gray-700 mb-4">{title}</h3>
      )}
      <div className="relative overflow-hidden rounded-lg shadow-lg h-64 sm:h-80 md:h-96">
        {images.map((imgUrl, index) => (
          <div
            key={index}
            className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${
              index === currentIndex ? "opacity-100 -z-10" : "opacity-0 z-0"
            }`}
            style={{ transitionDelay: index === currentIndex ? "0ms" : "0ms" }}
          >
            <img
              src={imgUrl}
              alt={`Project gallery image ${index + 1}`}
              className="w-full h-full object-cover"
            />
          </div>
        ))}
        {hasMultipleImages && (
          <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 -z-20 flex space-x-2">
            {images.map((_, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index)}
                aria-label={`Go to slide ${index + 1}`}
                className={`w-2.5 h-2.5 rounded-full transition-colors duration-300 ${
                  currentIndex === index
                    ? "bg-white"
                    : "bg-white/50 hover:bg-white/75"
                }`}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

ProjectCarousel.propTypes = {
  title: PropTypes.string,
  images: PropTypes.arrayOf(PropTypes.string).isRequired,
  autoplayDelay: PropTypes.number,
};

const ContentSectionRenderer = ({ section, index }) => {
  switch (section.type) {
    case "heading":
      const Tag = `h${section.level || 2}`;
      return (
        <Tag
          key={index}
          className="text-xl md:text-2xl font-semibold text-gray-800 mb-4 mt-6 first:mt-0"
        >
          {section.text}
        </Tag>
      );

    case "moduleList":
      return (
        <div key={index} className="mb-6 space-y-5">
          {section.modules?.map((group, groupIndex) => (
            <div key={groupIndex}>
              {group.title && (
                <h3 className="text-lg font-semibold text-blue-800 mb-2">
                  {group.title}
                </h3>
              )}
              {group.items?.length > 0 && (
                <ul className="list-disc list-outside space-y-1.5 text-gray-700 pl-5">
                  {group.items.map((item, itemIndex) => (
                    <li key={itemIndex}>{item}</li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>
      );

    case "paragraph":
      return (
        <p key={index} className="text-gray-700 mb-4">
          {section.text}
        </p>
      );

    case "achievement":
      return (
        <div
          key={index}
          className="mb-6 bg-blue-50 p-4 rounded-lg border border-blue-200 inline-block"
        >
          {section.title && (
            <h3 className="text-lg font-semibold text-blue-800 mb-2">
              {section.title}
            </h3>
          )}
          {section.value && (
            <p className="text-md text-gray-800 font-medium">{section.value}</p>
          )}
        </div>
      );

    case "gallery":
      return (
        <ProjectCarousel
          key={index}
          title={section.title}
          images={section.images}
        />
      );

    default:
      return null;
  }
};

ContentSectionRenderer.propTypes = {
  section: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
};

const ProjectDetailPage = () => {
  const { projectId } = useParams();
  const project = projectsData.find((p) => p.id.toString() === projectId);

  if (!project) {
    return <Navigate to="/projects" replace />;
  }

  return (
    <div>
      <div className="container mx-auto px-4 py-10 md:py-16 max-w-4xl font-montserrat">
        <Link
          to="/projects"
          className="text-blue-600 hover:underline mb-6 inline-block text-sm"
        >
          ← Ко всем курсам
        </Link>

        <h1 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
          {project.title}
        </h1>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 mb-6 text-sm text-gray-600 border-b pb-4">
          <p>
            <strong className="text-gray-700 font-medium">Место проведения:</strong>{" "}
            {project.city}
          </p>
          <p>
            <strong className="text-gray-700 font-medium">Период обучения:</strong>{" "}
            {project.date}
          </p>
          <p>
            <strong className="text-gray-700 font-medium">Категория слушателей:</strong>{" "}
            {project.participantCategory}
          </p>
          <p>
            <strong className="text-gray-700 font-medium">
              Кол-во слушателей:
            </strong>{" "}
            {project.participantCount}
          </p>
          <p className="sm:col-span-2 mt-2">
            <strong className="text-gray-700 font-medium">Цель:</strong>{" "}
            {project.aim}
          </p>
        </div>

        {project.contentSections &&
          Array.isArray(project.contentSections) &&
          project.contentSections.length > 0 && (
            <div className="mt-8">
              {project.contentSections.map((section, index) => (
                <ContentSectionRenderer
                  key={index}
                  section={section}
                  index={index}
                />
              ))}
            </div>
          )}

        {(!project.contentSections || project.contentSections.length === 0) && (
          <p className="text-gray-500 mt-8 italic">
            Дополнительная информация по этому курсу не указана.
          </p>
        )}
      </div>
      <Contacts />
    </div>
  );
};

export default ProjectDetailPage;
